// utils/enrichOffer.js

const fmtMoney = (n, code) =>
  code
    ? new Intl.NumberFormat(undefined, { style: "currency", currency: code }).format(n)
    : (Number.isFinite(n) ? n.toFixed(2) : "0.00");

const toAmount = (minor, divisor) => (minor ?? 0) / (divisor || 1);

/**
 * Enrich VOUCHER offer (handles nested or flattened price/send)
 * @param {object} offer
 * @param {object} brands - map like { "agenciasway": { brandName, image } }
 * @returns {object}
 */
export function enrichOffer(offer, brands = {}) {
  // Brand enrichment (case-insensitive key support)
  const brandKey = String(offer?.brand || "").toLowerCase();
  const brandData = brands[brandKey] || brands[offer?.brand] || null;

  // --- PRICE (what you charge; offer currency) ---
  const priceObj   = typeof offer?.price === "object" ? offer.price : {};
  const priceMinor =
    (typeof offer?.price === "number" ? offer.price : undefined) ??
    priceObj.fixed ??
    priceObj.min ??
    offer?.priceFixed ??
    offer?.priceMin ??
    0;

  const priceDiv =
    offer?.priceCurrencyDivisor ??
    priceObj.currencyDivisor ??
    1;

  const priceCurrency =
    offer?.priceCurrency ??
    priceObj.currency ??
    undefined;

  const priceAmount  = toAmount(priceMinor, priceDiv);
  const priceDisplay = fmtMoney(priceAmount, priceCurrency);

  // --- SEND / FACE VALUE (local destination currency) ---
  const sendObj   = typeof offer?.send === "object" ? offer.send : {};
  const sendMinor =
    (typeof offer?.send === "number" ? offer.send : undefined) ??
    sendObj.fixed ??
    sendObj.min ??
    offer?.sendFixed ??
    offer?.sendMin ??
    0;

  const sendDiv =
    offer?.sendCurrencyDivisor ??
    sendObj.currencyDivisor ??
    1;

  const sendCurrency =
    offer?.sendCurrency ??
    sendObj.currency ??
    undefined;

  const faceAmount  = toAmount(sendMinor, sendDiv);
  const faceDisplay = sendCurrency ? fmtMoney(faceAmount, sendCurrency) : null;
  const hasLocalFace = Boolean(sendCurrency && (sendMinor ?? 0) > 0);

  return {
    ...offer,

    // Brand fallbacks
    brandName: brandData?.brandName || offer?.brandName || offer?.brand || "Unknown Brand",
    image: brandData?.image ?? offer?.image ?? null,

    // Keep raw nested structures intact
    price: offer?.price,
    send: offer?.send,

    // Normalized, easy-to-render fields
    priceAmount,                // number (e.g., 480)
    priceCurrency,              // "USD"
    priceDisplay,               // "$480.00"

    faceAmount,                 // number (e.g., 3600)
    faceCurrency: sendCurrency, // "GTQ"
    faceDisplay,                // "Q3,600.00" or null
    hasLocalFace,

    // Back-compat simple strings if something still reads them
    priceDisplayRaw: priceAmount.toFixed(2),
    sendDisplayRaw: hasLocalFace ? faceAmount.toFixed(2) : null,
  };
}
